import cookie from 'js-cookie';
import { ApolloClient, from, InMemoryCache, split } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { ErrorLink } from '@apollo/client/link/error';
import { Context } from '@nuxt/types';

export interface APIClientConfig {
  apiUrl: string;
  apiV2Url: string;
  apiV2SubscriptionUrl: string;
  reportUrl: string;
  reportQuestionnaireUrl: string;
  subscriptionUrl: string;
  cryptoGraphqlKey: string;
  cryptoGraphqlSecret: string;
  cryptoGraphqlAlgorithm: string;
}

export type AsarenAPIClientConfig = Pick<
  APIClientConfig,
  | 'apiUrl'
  | 'subscriptionUrl'
  | 'cryptoGraphqlKey'
  | 'cryptoGraphqlSecret'
  | 'cryptoGraphqlAlgorithm'
>;

export class AsarenAPIClient {
  /* eslint-disable no-useless-constructor */
  constructor(
    private config: AsarenAPIClientConfig,
    private context: Context
  ) {}

  get token(): string {
    const tokenFromCookie = cookie.get('access-token');
    const tokenFromContext = this.context.app?.$auth?.getToken('local');
    const tokenFromLocalStorage =
      typeof window !== 'undefined'
        ? window.localStorage['auth._token.local']
        : null;
    return tokenFromContext || tokenFromCookie || tokenFromLocalStorage;
  }

  createApolloClient(): ApolloClient<any> {
    // Error handling link
    const errorLink = new ErrorLink((error) => {
      if (error.graphQLErrors) {
        let unauthorized = false;
        error.graphQLErrors.forEach((err) => {
          // eslint-disable-next-line no-console
          console.error(
            `[GraphQL Error] ${err.message} on "${err.path?.join(' > ')}"`
          );
          if (/Required authenticated user|not authorized/i.test(err.message)) {
            unauthorized = true;
          }
        });
        if (unauthorized) {
          this.context.$emitter.emit('NOT_AUTHORIZED');
        }
      }
    });

    // HTTP connection to the API
    const httpLink = createUploadLink({
      uri: this.config.apiUrl,
      fetch: (input: RequestInfo, init: any) => {
        // ssr problem, window is not defined in promise below
        const globalWindow = window;
        const token = this.token.replace('Bearer ', '');

        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
          const url = this.config.apiUrl || input;
          // set authorization header
          if (init.headers && init.headers.set) {
            /*
             * Ensure preflight requests are made for security reasons
             * This is important to prevent Cross-Site Request Forgery (CSRF) attacks
             * See Apollo Server documentation for more details:
             *
             * https://www.apollographql.com/docs/apollo-server/v3/security/cors#preventing-cross-site-request-forgery-csrf
             * https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#preflighted_requests
             *
             */
            init.headers.set('Apollo-Require-Preflight', true);
            init.headers.set('Authorization', `Bearer ${token}`);
          } else {
            init.headers = init.headers || ({} as any);
            /*
             * Ensure preflight requests are made for security reasons
             * This is important to prevent Cross-Site Request Forgery (CSRF) attacks
             * See Apollo Server documentation for more details:
             *
             * https://www.apollographql.com/docs/apollo-server/v3/security/cors#preventing-cross-site-request-forgery-csrf
             * https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#preflighted_requests
             *
             */
            init.headers['Apollo-Require-Preflight'] = true;
            init.headers.Authorization = `Bearer ${token}`;
          }

          let response = null;
          let body = null;
          try {
            response = await fetch(url, init);
            body = await response.json();
          } catch (error) {
            reject(error);
          }

          if (response === null || response === undefined) {
            resolve(response);
            return;
          }

          const isEncrypted = (
            response.headers.get('Content-Type') || ''
          ).includes('base64');

          if (isEncrypted) {
            try {
              const decrypted = await this.decrypt(
                body.data,
                globalWindow.crypto
              );
              Object.assign(body, {
                data: JSON.parse(decrypted)
              });
            } catch (error) {
              reject(error);
            }
          }

          response.text = () =>
            new Promise((resolve) => {
              resolve(JSON.stringify(body));
            });

          response.json = () =>
            new Promise((resolve) => {
              resolve(body);
            });

          resolve(response);
        });
      }
    });

    // Create the subscription websocket link
    const wsLink = process.client
      ? new GraphQLWsLink(
          createClient({
            url: this.config.subscriptionUrl,
            shouldRetry: () => true,
            retryAttempts: 5,
            connectionParams: () => ({
              token: this.token
            }),
            on: {
              /**
               * WebSocket event listeners.
               */
              connected: () => {
                // eslint-disable-next-line no-console
                console.debug(`WebSocket connected.`);
              },
              /**
               * WebSocket closed listener.
               *
               * @param {unknown} event - Close event.
               */
              closed: (event: any) => {
                // eslint-disable-next-line no-console
                console.warn(
                  `WebSocket closed. Code: ${event.code}, Reason: ${event.reason}`
                );
              },
              /**
               * WebSocket error listener.
               *
               * @param {unknown} error - Error object.
               */
              error: (error: any) => {
                // eslint-disable-next-line no-console
                console.error(`WebSocket error:`, error);
              },
              /**
               * WebSocket ping listener.
               *
               * @param {boolean} received - Whether the ping was received.
               */
              ping: (received) => {
                // eslint-disable-next-line no-console
                console.debug(
                  `WebSocket ping ${received ? 'received' : 'sent'}.`
                );
              },
              /**
               * WebSocket pong listener.
               *
               * @param {boolean} received - Whether the pong was received.
               */
              pong: (received) => {
                // eslint-disable-next-line no-console
                console.debug(
                  `WebSocket pong ${received ? 'received' : 'sent'}.`
                );
              }
            }
          })
        )
      : null;

    // using the ability to split links, you can send data to each link
    // depending on what kind of operation is being sent
    const link = process.server
      ? httpLink
      : split(
          // split based on operation type
          ({ query }) => {
            const definition = getMainDefinition(query);
            return (
              definition.kind === 'OperationDefinition' &&
              definition.operation === 'subscription'
            );
          },
          wsLink,
          httpLink
        );

    // Cache implementation
    const cache = new InMemoryCache({
      typePolicies: {
        PurchaseProductKitReport: {
          merge: true
        }
      }
    });

    // Create apollo client
    return new ApolloClient({
      link: from([errorLink, link]),
      cache,
      defaultOptions: {
        query: {
          fetchPolicy: 'network-only'
        }
      }
    });
  }

  async decrypt(data: string, crypto: Crypto = window.crypto): Promise<any> {
    try {
      const algorithm = this.config.cryptoGraphqlAlgorithm;

      const iv = new TextEncoder().encode(this.config.cryptoGraphqlSecret);
      const key = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(this.config.cryptoGraphqlKey),
        algorithm,
        true,
        ['decrypt']
      );

      const buffer = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));

      const decrypt = await crypto.subtle.decrypt(
        {
          name: algorithm,
          iv,
          tagLength: 128
        },
        key,
        buffer
      );

      return new TextDecoder().decode(decrypt);
    } catch (error) {
      throw new Error('Failed to decrypt graphql response data');
    }
  }
}
